import { useEffect, useMemo, useState } from 'react'
import { useLingui } from '@lingui/react'
import { useAppSelector } from '@/redux/rootReducer'
import { skipToken } from '@reduxjs/toolkit/query'
import { useOpenSessionMutation, useSessionExistsQuery } from '@/core/services/dmp-connect-js2'
import { selectDMPConnectDcParameter, selectDMPConnectSessionId } from '@/redux/reducers/dmp-connect.reducer'
import { t } from '@lingui/macro'

export function useDmpConnectSession() {
  const lingui = useLingui()
  const dmpConnectSessionId = useAppSelector(selectDMPConnectSessionId)
  const dcParameter = useAppSelector(selectDMPConnectDcParameter)

  const { data: sessionExists, error: sessionExistsError } = useSessionExistsQuery(dmpConnectSessionId ?? skipToken, {
    refetchOnMountOrArgChange: true,
    selectFromResult: ({ data, ...rest }) => ({ ...rest, data: data?.i_exists }),
  })

  const [openSession, { error: openSessionError }] = useOpenSessionMutation()

  const [isLoadingSession, setIsLoadingSession] = useState(sessionExists !== 1)

  const error = useMemo(() => {
    if (sessionExistsError) return lingui._(t`Failed to check DMP Connect session`)
    if (openSessionError) return lingui._(t`Failed to open DMP Connect session`)

    return undefined
  }, [lingui, sessionExistsError, openSessionError])

  useEffect(() => {
    if (!dcParameter) return

    if (sessionExists === 1) {
      setIsLoadingSession(false)
      return
    }

    if (!dmpConnectSessionId || sessionExists === 0) {
      openSession({ s_dcparameters64: dcParameter })
        .unwrap()
        .then(() => setIsLoadingSession(false))
        .catch(() => setIsLoadingSession(false))
    }
  }, [dcParameter, dmpConnectSessionId, openSession, sessionExists])

  return { error, isLoadingSession, sessionExists }
}
